import React, { useState, useEffect } from "react";
import styles from "./SettingsPane.module.css";

// Indentation used to nest the merchant ID <optgroup> labels and <option> text
const GROUP_INDENT = "    "; // &nbsp; x4
const OPTION_INDENT = " "; // &ensp;

// Apple Pay merchant IDs, grouped by environment/encryption method and Checkout.com account
const APPLE_PAY_MERCHANT_GROUPS = [
  {
    label: "Checkout.com Sandbox | ECC",
    accounts: [
      {
        label: "Stephen Gilbert INC | cli_6u2qiojifotuxigo2rya4w3bny",
        merchantIds: ["merchant.wallets.test.suite"],
      },
      {
        label: "Gateway QA | cli_iysvilmom3qe5g6tycjf73w2iu",
        merchantIds: ["merchant.sandbox.gateway"],
      },
      {
        label: "Gateway E2E Automated Tests | cli_7st3kctzfa7ulc5d6ntk65x45q",
        merchantIds: ["merchant.sandbox.e2e"],
      },
    ],
  },
  {
    label: "Checkout.com Sandbox | RSA",
    accounts: [
      {
        label: "Apple Pay China Test | cli_z2vtynlx3scurbbev3sbc6ntp4",
        merchantIds: ["merchant.china.mainland.rsa"],
      },
    ],
  },
  {
    label: "Checkout.com QA | ECC",
    accounts: [
      {
        label:
          "* Gateway QA E2E (Do Not Modify!) | cli_ntdwmvhphzmezgzjkrvf77mnyq",
        merchantIds: ["merchant.qa.stephen"],
      },
    ],
  },
  {
    label: "Checkout.com Production | ECC",
    accounts: [
      {
        label: "e2e-test-account | cli_4ucqjcx6mmvurfetoqihc7lary",
        merchantIds: ["merchant.prod.stephen"],
      },
      // {
      //   label: "MALPB Test Account 1 | cli_nb653227glgubjvlagnsnpt3my",
      //   merchantIds: ["merchant.prod.malpb"],
      // },
    ],
  },
  {
    label: "KNET Sandbox | ECC",
    accounts: [
      {
        label: "APM Test | cli_memowvltf7aulpb3poehtiffei",
        merchantIds: ["merchant.knet.test", "merchant.knet.gap.test"],
      },
    ],
  },
  {
    label: "QPay Sandbox | ECC",
    accounts: [
      {
        label: "APM Test | cli_memowvltf7aulpb3poehtiffei",
        merchantIds: ["merchant.qpay.test", "merchant.qpay.test2"],
      },
    ],
  },
  {
    label: "Blue EMI Production | ECC",
    accounts: [
      {
        label: "e2e-test-account-blueemi | cli_iqimxaln4oeuhl2cgzl2z2istm",
        merchantIds: ["merchant.blue.emi.prod"],
      },
    ],
  },
  {
    label: "Test Decryption | ECC",
    accounts: [{ merchantIds: ["merchant.test.decryption"] }],
  },
];

// Shared merchant ID options, used by main and multi-token dropdowns
const MerchantIdOptions = () =>
  APPLE_PAY_MERCHANT_GROUPS.map((group) => (
    <React.Fragment key={group.label}>
      <optgroup label={group.label}></optgroup>
      {group.accounts.map((account, index) => {
        const options = account.merchantIds.map((merchantId) => (
          <option key={merchantId} value={merchantId}>
            {`${OPTION_INDENT}${merchantId}`}
          </option>
        ));
        return (
          <React.Fragment key={account.label || `account-${index}`}>
            {account.label ? (
              <optgroup label={`${GROUP_INDENT}${account.label}`}>
                {options}
              </optgroup>
            ) : (
              options
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  ));

// Payment types available on a standard ApplePayPaymentRequest
const STANDARD_APPLE_PAY_TYPES = ["final", "pending"];

const SettingsPane = ({
  isSidebar,
  onClose,
  applePaySettings,
  onApplePaySettingsChange,
  googlePaySettings,
  onGooglePaySettingsChange,
  samsungPaySettings,
  onSamsungPaySettingsChange,
}) => {
  const wrapperClass = isSidebar ? "sidebarWrapper" : "container";
  const paneClass = isSidebar ? "sidebar" : "modalContent";
  const [activeTab, setActiveTab] = useState("applePay");

  // Local state to manage Apple Pay settings
  const [localApplePaySettings, setLocalApplePaySettings] =
    useState(applePaySettings);

  // Local state to manage Google Pay settings
  const [localGooglePaySettings, setLocalGooglePaySettings] =
    useState(googlePaySettings);

  // Local state to manage Samsung Pay settings
  const [localSamsungPaySettings, setLocalSamsungPaySettings] =
    useState(samsungPaySettings);

  useEffect(() => {
    setLocalApplePaySettings(applePaySettings);
  }, [applePaySettings]);

  useEffect(() => {
    setLocalGooglePaySettings(googlePaySettings);
  }, [googlePaySettings]);

  useEffect(() => {
    setLocalSamsungPaySettings(samsungPaySettings);
  }, [samsungPaySettings]);

  const multiTokenContexts = localApplePaySettings.multiTokenContexts || [];

  const applyApplePaySettings = (updatedSettings) => {
    setLocalApplePaySettings(updatedSettings);
    onApplePaySettingsChange(updatedSettings); // Update parent state
  };

  // With multi-token enabled, the total is the sum of the merchant amounts
  const withMultiTokenTotal = (settings) =>
    settings.multiToken
      ? {
          ...settings,
          amount: (settings.multiTokenContexts || [])
            .reduce(
              (total, context) => total + (Number(context.amount) || 0),
              0,
            )
            .toFixed(2),
        }
      : settings;

  const handleApplePayChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updatedSettings = {
      ...localApplePaySettings,
      [name]: type === "checkbox" ? checked : value,
    };
    if (!updatedSettings.requireShippingContact) {
      updatedSettings = { ...updatedSettings, requireShippingMethod: false };
    }
    // Multi-token is only supported on a standard ApplePayPaymentRequest
    if (
      name === "multiToken" &&
      checked &&
      !STANDARD_APPLE_PAY_TYPES.includes(updatedSettings.paymentType)
    ) {
      updatedSettings = { ...updatedSettings, paymentType: "final" };
    }
    applyApplePaySettings(withMultiTokenTotal(updatedSettings));
  };

  const handleMultiTokenContextChange = (index, field, value) => {
    applyApplePaySettings(
      withMultiTokenTotal({
        ...localApplePaySettings,
        multiTokenContexts: multiTokenContexts.map((context, i) =>
          i === index ? { ...context, [field]: value } : context,
        ),
      }),
    );
  };

  const addMultiTokenContext = () => {
    const lastContext = multiTokenContexts[multiTokenContexts.length - 1];
    applyApplePaySettings(
      withMultiTokenTotal({
        ...localApplePaySettings,
        multiTokenContexts: [
          ...multiTokenContexts,
          {
            merchantId:
              lastContext?.merchantId || localApplePaySettings.merchantId,
            amount: "1.00",
          },
        ],
      }),
    );
  };

  const removeMultiTokenContext = (index) => {
    applyApplePaySettings(
      withMultiTokenTotal({
        ...localApplePaySettings,
        multiTokenContexts: multiTokenContexts.filter((_, i) => i !== index),
      }),
    );
  };

  const handleGooglePayChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updatedSettings = {
      ...localGooglePaySettings,
      [name]: type === "checkbox" ? checked : value,
    };
    if (!updatedSettings.requireShippingContact) {
      updatedSettings = { ...updatedSettings, requireShippingMethod: false };
    }

    setLocalGooglePaySettings(updatedSettings);
    onGooglePaySettingsChange(updatedSettings); // Update parent state
  };

  const handleSamsungPayChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedSettings = {
      ...localSamsungPaySettings,
      [name]: type === "checkbox" ? checked : value,
    };

    setLocalSamsungPaySettings(updatedSettings);
    onSamsungPaySettingsChange(updatedSettings); // Update parent state
  };

  // MPAN payment types require token lifecycle + management URLs
  const MPAN_PAYMENT_TYPES = ["recurring", "deferred", "autoReload"];
  const isMpanApplePayType = MPAN_PAYMENT_TYPES.includes(
    localApplePaySettings.paymentType,
  );
  const isMpanGooglePayType = MPAN_PAYMENT_TYPES.includes(
    localGooglePaySettings.paymentType,
  );

  // Multi-token (multi-merchant) support
  const isMultiToken = localApplePaySettings.multiToken;

  // Brazil combo card support
  const showBrazilCreditNetworks =
    localGooglePaySettings.country?.toUpperCase() === "BR" &&
    localGooglePaySettings.allowCredit;

  return (
    <div className={styles[wrapperClass]}>
      {isSidebar ? null : (
        <div className={styles.modalOverlay} onClick={onClose} />
      )}{" "}
      {/* Overlay only when not a sidebar */}
      <div className={styles[paneClass]}>
        <div className={styles.titleBar}>
          {isSidebar ? null : (
            <button className={styles.closeButton} onClick={onClose}>
              &times;
            </button>
          )}{" "}
          <h3>Configuration</h3>
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          <button
            className={activeTab === "applePay" ? styles.active : ""}
            onClick={() => setActiveTab("applePay")}
          >
            Apple Pay
          </button>
          <button
            className={activeTab === "googlePay" ? styles.active : ""}
            onClick={() => setActiveTab("googlePay")}
          >
            Google Pay
          </button>
          <button
            className={activeTab === "samsungPay" ? styles.active : ""}
            onClick={() => setActiveTab("samsungPay")}
          >
            Samsung Pay
          </button>
        </div>
        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === "applePay" && (
            <section>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Merchant ID:</label>
                  <select
                    name="merchantId"
                    value={localApplePaySettings.merchantId}
                    onChange={handleApplePayChange}
                  >
                    <MerchantIdOptions />
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <a
                      href="https://developer.apple.com/documentation/applepayontheweb/requesting-an-apple-pay-payment-session#Provide-request-parameters"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Initiative Context
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="initiativeContext"
                    value={localApplePaySettings.initiativeContext}
                    onChange={handleApplePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Merchant&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/country-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Country
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={localApplePaySettings.country}
                    onChange={handleApplePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>MCC:</label>
                  <input
                    type="text"
                    name="mcc"
                    value={localApplePaySettings.mcc}
                    onChange={handleApplePayChange}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Payment&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/currency-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Currency
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="currency"
                    value={localApplePaySettings.currency}
                    onChange={handleApplePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Amount:</label>
                  <input
                    type="number"
                    name="amount"
                    value={localApplePaySettings.amount}
                    onChange={handleApplePayChange}
                    disabled={isMultiToken}
                    title={
                      isMultiToken
                        ? "Total is the sum of the multi-token merchant amounts"
                        : undefined
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Type:</label>
                  <select
                    name="paymentType"
                    value={localApplePaySettings.paymentType}
                    onChange={handleApplePayChange}
                  >
                    <optgroup label="DPAN"></optgroup>
                    <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;ApplePayPaymentRequest">
                      <option value="final">&ensp;Final</option>
                      <option value="pending">&ensp;Pending</option>
                    </optgroup>
                    {!isMultiToken && (
                      <>
                        <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;Disbursement Request">
                          <option value="disbursement">&ensp;Payout</option>
                        </optgroup>
                        <optgroup label="MPAN"></optgroup>
                        <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;ApplePayRecurringPaymentRequest">
                          <option value="recurring">&ensp;Recurring</option>
                        </optgroup>
                        <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;ApplePayDeferredPaymentRequest">
                          <option value="deferred">&ensp;Deferred</option>
                        </optgroup>
                        <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;ApplePayAutomaticReloadPaymentRequest">
                          <option value="autoReload">
                            &ensp;Automatic Top-up
                          </option>
                        </optgroup>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {isMpanApplePayType && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Token Notification URL:
                    </label>
                    <input
                      type="text"
                      name="tokenNotificationURL"
                      value={localApplePaySettings.tokenNotificationURL}
                      onChange={handleApplePayChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Management URL:</label>
                    <input
                      type="text"
                      name="managementURL"
                      value={localApplePaySettings.managementURL}
                      onChange={handleApplePayChange}
                    />
                  </div>
                </div>
              )}

              <div
                className={`${styles.checkboxGroup} ${
                  isMultiToken ? "" : styles.collapsedSection
                }`}
              >
                <div className={styles.sectionHeading}>
                  <label
                    className={styles.sectionHeadingLabel}
                    htmlFor="multiToken"
                  >
                    <a
                      href="https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/multitokencontexts"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Multi-token
                    </a>
                    :
                  </label>
                  <input
                    type="checkbox"
                    name="multiToken"
                    checked={isMultiToken}
                    onChange={handleApplePayChange}
                    id="multiToken"
                  />
                </div>
                {isMultiToken && (
                  <>
                    {multiTokenContexts.map((context, index) => (
                      <div
                        className={`${styles.formRow} ${styles.contextRow}`}
                        key={index}
                      >
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>
                            Merchant ID {index + 1}:
                          </label>
                          <select
                            value={context.merchantId}
                            onChange={(e) =>
                              handleMultiTokenContextChange(
                                index,
                                "merchantId",
                                e.target.value,
                              )
                            }
                          >
                            <MerchantIdOptions />
                          </select>
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Amount:</label>
                          <input
                            type="number"
                            step="0.01"
                            value={context.amount}
                            onChange={(e) =>
                              handleMultiTokenContextChange(
                                index,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => removeMultiTokenContext(index)}
                          disabled={multiTokenContexts.length <= 1}
                          title="Remove merchant"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    <div className={styles.contextFooter}>
                      <button
                        type="button"
                        className={styles.addContextButton}
                        onClick={addMultiTokenContext}
                      >
                        + Add merchant
                      </button>
                      <span className={styles.contextTotal}>
                        Total: {localApplePaySettings.amount}{" "}
                        {localApplePaySettings.currency}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className={styles.checkboxGroup}>
                <h4>Allowed networks</h4>
                <div className={`${styles.formRow} ${styles.network}`}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowVisa"
                      checked={localApplePaySettings.allowVisa}
                      onChange={handleApplePayChange}
                      id="allowVisa"
                    />
                    <label className={styles.formLabel} htmlFor="allowVisa">
                      Visa
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMastercard"
                      checked={localApplePaySettings.allowMastercard}
                      onChange={handleApplePayChange}
                      id="allowMastercard"
                    />
                    <label
                      className={styles.formLabel}
                      htmlFor="allowMastercard"
                    >
                      Mastercard
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMaestro"
                      checked={localApplePaySettings.allowMaestro}
                      onChange={handleApplePayChange}
                      id="allowMaestro"
                    />
                    <label className={styles.formLabel} htmlFor="allowMaestro">
                      Maestro
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowAmex"
                      checked={localApplePaySettings.allowAmex}
                      onChange={handleApplePayChange}
                      id="allowAmex"
                    />
                    <label className={styles.formLabel} htmlFor="allowAmex">
                      Amex
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowDiscover"
                      checked={localApplePaySettings.allowDiscover}
                      onChange={handleApplePayChange}
                      id="allowDiscover"
                    />
                    <label className={styles.formLabel} htmlFor="allowDiscover">
                      Discover
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowJcb"
                      checked={localApplePaySettings.allowJcb}
                      onChange={handleApplePayChange}
                      id="allowJcb"
                    />
                    <label className={styles.formLabel} htmlFor="allowJcb">
                      JCB
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowUnionPay"
                      checked={localApplePaySettings.allowUnionPay}
                      onChange={handleApplePayChange}
                      id="allowUnionPay"
                    />
                    <label className={styles.formLabel} htmlFor="allowUnionPay">
                      UnionPay
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowCb"
                      checked={localApplePaySettings.allowCb}
                      onChange={handleApplePayChange}
                      id="allowCb"
                    />
                    <label className={styles.formLabel} htmlFor="allowCb">
                      CB
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMada"
                      checked={localApplePaySettings.allowMada}
                      onChange={handleApplePayChange}
                      id="allowMada"
                    />
                    <label className={styles.formLabel} htmlFor="allowMada">
                      mada
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowJaywan"
                      checked={localApplePaySettings.allowJaywan}
                      onChange={handleApplePayChange}
                      id="allowJaywan"
                    />
                    <label className={styles.formLabel} htmlFor="allowJaywan">
                      Jaywan
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowEftpos"
                      checked={localApplePaySettings.allowEftpos}
                      onChange={handleApplePayChange}
                      id="allowEftpos"
                    />
                    <label className={styles.formLabel} htmlFor="allowEftpos">
                      eftpos
                    </label>
                  </div>
                </div>
                <h4>Allowed cards</h4>
                <div className={`${styles.formRow} ${styles.cardType}`}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Debit:</label>
                    <input
                      type="checkbox"
                      name="allowDebit"
                      checked={localApplePaySettings.allowDebit}
                      onChange={handleApplePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Credit:</label>
                    <input
                      type="checkbox"
                      name="allowCredit"
                      checked={localApplePaySettings.allowCredit}
                      onChange={handleApplePayChange}
                    />
                  </div>
                </div>
                <h4>Optional requirements</h4>
                <div className={styles.formRow}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Billing Contact:</label>
                    <input
                      type="checkbox"
                      name="requireBillingContact"
                      checked={localApplePaySettings.requireBillingContact}
                      onChange={handleApplePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>
                      Shipping Contact:
                    </label>
                    <input
                      type="checkbox"
                      name="requireShippingContact"
                      checked={localApplePaySettings.requireShippingContact}
                      onChange={handleApplePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Shipping Method:</label>
                    <input
                      type="checkbox"
                      name="requireShippingMethod"
                      checked={localApplePaySettings.requireShippingMethod}
                      disabled={!localApplePaySettings.requireShippingContact}
                      onChange={handleApplePayChange}
                    />
                  </div>
                </div>
              </div>

              <div className={`${styles.formRow} ${styles.buttonConfig}`}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Button Type:</label>
                  <select
                    name="buttonType"
                    value={localApplePaySettings.buttonType}
                    onChange={handleApplePayChange}
                  >
                    <option value="plain">Plain</option>
                    <option value="add-money">Add money</option>
                    <option value="book">Book</option>
                    <option value="buy">Buy</option>
                    <option value="check-out">Check out</option>
                    <option value="continue">Continue</option>
                    <option value="contribute">Contribute</option>
                    <option value="donate">Donate</option>
                    <option value="order">Order</option>
                    <option value="reload">Reload</option>
                    <option value="rent">Rent</option>
                    <option value="subscribe">Subscribe</option>
                    <option value="support">Support</option>
                    <option value="tip">Tip</option>
                    <option value="top-up">Top Up</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Button Style:</label>
                  <select
                    name="buttonStyle"
                    value={localApplePaySettings.buttonStyle}
                    onChange={handleApplePayChange}
                  >
                    <option value="black">Black</option>
                    <option value="white">White</option>
                    <option value="white-outline">White with outline</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Button&nbsp;
                    <a
                      href="https://developer.apple.com/documentation/apple_pay_on_the_web/applepaybuttonlocale"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Language
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="buttonLanguage"
                    value={localApplePaySettings.buttonLanguage}
                    onChange={handleApplePayChange}
                  />
                </div>
              </div>
            </section>
          )}

          {activeTab === "googlePay" && (
            <section>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Merchant ID:</label>
                  <input
                    type="text"
                    name="merchantId"
                    value={localGooglePaySettings.merchantId}
                    onChange={handleGooglePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Gateway Merchant ID:
                  </label>
                  <input
                    type="text"
                    name="gatewayMerchantId"
                    value={localGooglePaySettings.gatewayMerchantId}
                    onChange={handleGooglePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Acquirer&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/country-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Country
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={localGooglePaySettings.country}
                    onChange={handleGooglePayChange}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Payment&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/currency-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Currency
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="currency"
                    value={localGooglePaySettings.currency}
                    onChange={handleGooglePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Amount:</label>
                  <input
                    type="number"
                    name="amount"
                    value={localGooglePaySettings.amount}
                    onChange={handleGooglePayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Type:</label>
                  <select
                    name="paymentType"
                    value={localGooglePaySettings.paymentType}
                    onChange={handleGooglePayChange}
                  >
                    <optgroup label="DPAN"></optgroup>
                    <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;TransactionInfo">
                      <option value="transaction">&ensp;Standard</option>
                    </optgroup>
                    <optgroup label="MPAN"></optgroup>
                    <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;RecurringTransactionInfo">
                      <option value="recurring">&ensp;Recurring</option>
                    </optgroup>
                    <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;DeferredTransactionInfo">
                      <option value="deferred">&ensp;Deferred</option>
                    </optgroup>
                    <optgroup label="&nbsp;&nbsp;&nbsp;&nbsp;AutomaticReloadTransactionInfo">
                      <option value="autoReload">&ensp;Automatic Top-up</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {isMpanGooglePayType && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Token Update URL:
                    </label>
                    <input
                      type="text"
                      name="tokenUpdateUrl"
                      value={localGooglePaySettings.tokenUpdateUrl}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Management URL:</label>
                    <input
                      type="text"
                      name="managementUrl"
                      value={localGooglePaySettings.managementUrl}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                </div>
              )}

              <div className={styles.checkboxGroup}>
                <h4>Allowed networks</h4>
                <div className={`${styles.formRow} ${styles.network}`}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowVisa"
                      checked={localGooglePaySettings.allowVisa}
                      onChange={handleGooglePayChange}
                      id="allowVisa"
                    />
                    <label className={styles.formLabel} htmlFor="allowVisa">
                      Visa
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMastercard"
                      checked={localGooglePaySettings.allowMastercard}
                      onChange={handleGooglePayChange}
                      id="allowMastercard"
                    />
                    <label
                      className={styles.formLabel}
                      htmlFor="allowMastercard"
                    >
                      Mastercard
                    </label>
                  </div>

                  {showBrazilCreditNetworks && (
                    <>
                      <div
                        className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                      >
                        <input
                          type="checkbox"
                          name="allowMaestro"
                          checked={localGooglePaySettings.allowMaestro}
                          onChange={handleGooglePayChange}
                          id="allowMaestro"
                        />
                        <label
                          className={styles.formLabel}
                          htmlFor="allowMaestro"
                        >
                          Maestro
                        </label>
                      </div>
                    </>
                  )}
                  {/* {showBrazilCreditNetworks && (
                    <>
                      <div
                        className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                      >
                        <input
                          type="checkbox"
                          name="allowElo"
                          checked={localGooglePaySettings.allowElo}
                          onChange={handleGooglePayChange}
                          id="allowElo"
                        />
                        <label className={styles.formLabel} htmlFor="allowElo">Elo</label>
                      </div>
                    </>
                  )} */}
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowAmex"
                      checked={localGooglePaySettings.allowAmex}
                      onChange={handleGooglePayChange}
                      id="allowAmex"
                    />
                    <label className={styles.formLabel} htmlFor="allowAmex">
                      Amex
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowDiscover"
                      checked={localGooglePaySettings.allowDiscover}
                      onChange={handleGooglePayChange}
                      id="allowDiscover"
                    />
                    <label className={styles.formLabel} htmlFor="allowDiscover">
                      Discover
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowJcb"
                      checked={localGooglePaySettings.allowJcb}
                      onChange={handleGooglePayChange}
                      id="allowJcb"
                    />
                    <label className={styles.formLabel} htmlFor="allowJcb">
                      JCB
                    </label>
                  </div>
                </div>
                <h4>Allowed cards</h4>
                <div className={`${styles.formRow} ${styles.cardType}`}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>CRYPTOGRAM_3DS:</label>
                    <input
                      type="checkbox"
                      name="allowCryptogram3ds"
                      checked={localGooglePaySettings.allowCryptogram3ds}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>PAN_ONLY:</label>
                    <input
                      type="checkbox"
                      name="allowPanOnly"
                      checked={localGooglePaySettings.allowPanOnly}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Prepaid:</label>
                    <input
                      type="checkbox"
                      name="allowPrepaid"
                      checked={localGooglePaySettings.allowPrepaid}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Credit:</label>
                    <input
                      type="checkbox"
                      name="allowCredit"
                      checked={localGooglePaySettings.allowCredit}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                </div>
                <h4>Optional requirements</h4>
                <div className={styles.formRow}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Billing Contact:</label>
                    <input
                      type="checkbox"
                      name="requireBillingContact"
                      checked={localGooglePaySettings.requireBillingContact}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>
                      Shipping Contact:
                    </label>
                    <input
                      type="checkbox"
                      name="requireShippingContact"
                      checked={localGooglePaySettings.requireShippingContact}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox}`}
                  >
                    <label className={styles.formLabel}>Shipping Method:</label>
                    <input
                      type="checkbox"
                      name="requireShippingMethod"
                      checked={localGooglePaySettings.requireShippingMethod}
                      disabled={!localGooglePaySettings.requireShippingContact}
                      onChange={handleGooglePayChange}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Button Type:</label>
                  <select
                    name="buttonType"
                    value={localGooglePaySettings.buttonType}
                    onChange={handleGooglePayChange}
                  >
                    <option value="plain">Plain</option>
                    <option value="book">Book</option>
                    <option value="buy">Buy</option>
                    <option value="checkout">Checkout</option>
                    <option value="donate">Donate</option>
                    <option value="order">Order</option>
                    <option value="pay">Pay</option>
                    <option value="subscribe">Subscribe</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Button Style:</label>
                  <select
                    name="buttonStyle"
                    value={localGooglePaySettings.buttonStyle}
                    onChange={handleGooglePayChange}
                  >
                    <option value="default">Default</option>
                    <option value="black">Black</option>
                    <option value="white">White</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Language:</label>
                  <select
                    name="buttonLanguage"
                    value={localGooglePaySettings.buttonLanguage}
                    onChange={handleGooglePayChange}
                  >
                    <option value="en">English</option>
                    <option value="ar">Arabic</option>
                    <option value="bg">Bulgarian</option>
                    <option value="ca">Catalan</option>
                    <option value="zh">Chinese</option>
                    <option value="hr">Croatian</option>
                    <option value="cs">Czech</option>
                    <option value="da">Danish</option>
                    <option value="nl">Dutch</option>
                    <option value="et">Estonian</option>
                    <option value="fi">Finnish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="el">Greek</option>
                    <option value="id">Indonesian</option>
                    <option value="it">Italian</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="ms">Malay</option>
                    <option value="no">Norwegian</option>
                    <option value="pl">Polish</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="sr">Serbian</option>
                    <option value="sk">Slovak</option>
                    <option value="sl">Slovenian</option>
                    <option value="es">Spanish</option>
                    <option value="sv">Swedish</option>
                    <option value="th">Thai</option>
                    <option value="tr">Turkish</option>
                    <option value="uk">Ukrainian</option>
                  </select>
                </div>
              </div>
            </section>
          )}

          {activeTab === "samsungPay" && (
            <section>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <a
                      href="https://developer.samsung.com/pay/web-sdk/create-service.html"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Service ID
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="serviceId"
                    value={localSamsungPaySettings.serviceId}
                    onChange={handleSamsungPayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Merchant ID:</label>
                  <input
                    type="text"
                    name="merchantId"
                    value={localSamsungPaySettings.merchantId}
                    onChange={handleSamsungPayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Merchant Name:</label>
                  <input
                    type="text"
                    name="merchantName"
                    value={localSamsungPaySettings.merchantName}
                    onChange={handleSamsungPayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Merchant&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/country-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Country
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={localSamsungPaySettings.country}
                    onChange={handleSamsungPayChange}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Payment&nbsp;
                    <a
                      href="https://www.checkout.com/docs/developer-resources/codes/currency-codes"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Currency
                    </a>
                    :
                  </label>
                  <input
                    type="text"
                    name="currency"
                    value={localSamsungPaySettings.currency}
                    onChange={handleSamsungPayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Amount:</label>
                  <input
                    type="number"
                    name="amount"
                    value={localSamsungPaySettings.amount}
                    onChange={handleSamsungPayChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Type:</label>
                  <select
                    name="paymentType"
                    value={localSamsungPaySettings.paymentType}
                    onChange={handleSamsungPayChange}
                  >
                    <option value="PURCHASE">Purchase</option>
                    <option value="PREORDER">Pre-order</option>
                    <option value="RESERVATION">Reservation</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Environment:</label>
                  <select
                    name="environment"
                    value={localSamsungPaySettings.environment}
                    onChange={handleSamsungPayChange}
                  >
                    <option value="STAGE">Stage</option>
                    <option value="PRODUCTION">Production</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Protocol:</label>
                  <select
                    name="protocol"
                    value={localSamsungPaySettings.protocol}
                    onChange={handleSamsungPayChange}
                  >
                    <option value="PROTOCOL_3DS">3DS</option>
                    <option value="PROTOCOL_EMV">EMV</option>
                  </select>
                </div>
              </div>

              <div className={styles.checkboxGroup}>
                <h4>Allowed networks</h4>
                <div className={`${styles.formRow} ${styles.network}`}>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowVisa"
                      checked={localSamsungPaySettings.allowVisa}
                      onChange={handleSamsungPayChange}
                      id="allowVisa"
                    />
                    <label className={styles.formLabel} htmlFor="allowVisa">
                      Visa
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMastercard"
                      checked={localSamsungPaySettings.allowMastercard}
                      onChange={handleSamsungPayChange}
                      id="allowMastercard"
                    />
                    <label
                      className={styles.formLabel}
                      htmlFor="allowMastercard"
                    >
                      Mastercard
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowAmex"
                      checked={localSamsungPaySettings.allowAmex}
                      onChange={handleSamsungPayChange}
                      id="allowAmex"
                    />
                    <label className={styles.formLabel} htmlFor="allowAmex">
                      Amex
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowDiscover"
                      checked={localSamsungPaySettings.allowDiscover}
                      onChange={handleSamsungPayChange}
                      id="allowDiscover"
                    />
                    <label className={styles.formLabel} htmlFor="allowDiscover">
                      Discover
                    </label>
                  </div>
                  <div
                    className={`${styles.formGroup} ${styles.compactCheckbox} ${styles.network}`}
                  >
                    <input
                      type="checkbox"
                      name="allowMada"
                      checked={localSamsungPaySettings.allowMada}
                      onChange={handleSamsungPayChange}
                      id="allowMada"
                    />
                    <label className={styles.formLabel} htmlFor="allowMada">
                      mada
                    </label>
                  </div>
                </div>
              </div>

              <div className={`${styles.formRow} ${styles.buttonConfig}`}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Button Style:</label>
                  <select
                    name="buttonStyle"
                    value={localSamsungPaySettings.buttonStyle}
                    onChange={handleSamsungPayChange}
                  >
                    <option value="black">Black</option>
                    <option value="white">White</option>
                  </select>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPane;
